import React, { useState } from 'react';
import { Bell, CheckCheck, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area"; 
import { formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationItem = ({ notification, onNotificationClick, onDeleteNotification }) => {
  return (
    <DropdownMenuItem
      className={`flex flex-col items-start p-3 hover:bg-accent focus:bg-accent cursor-pointer ${!notification.read_status ? 'bg-primary/5' : ''}`}
      aria-label={`Notification: ${notification.message}`}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <p
          className={`text-sm mb-1 flex-1 ${!notification.read_status ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
          onClick={() => onNotificationClick(notification)}
        >
          {notification.message}
        </p>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 p-0 ml-2 text-muted-foreground hover:text-destructive"
          aria-label="Delete notification"
          onClick={(e) => { e.stopPropagation(); onDeleteNotification(notification.id); }}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <span className="text-xs text-muted-foreground">
        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
      </span>
    </DropdownMenuItem>
  );
};

const Notifications = ({ notifications, unreadCount, onNotificationClick, onClearAll, onDeleteNotification }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 20 }}
              className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white"
            >
              {unreadCount}
            </motion.div>
          )}
          <span className="sr-only">Open notifications</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 md:w-96 p-0">
        <DropdownMenuLabel className="flex items-center justify-between p-3">
          <span className="font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Mark all as read clicked, unreadCount:', unreadCount);
                onClearAll();
              }} 
              className="text-xs h-auto py-1 px-2"
            >
              <CheckCheck className="mr-1 h-3 w-3" /> Mark all as read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="h-[300px]">
          {notifications && notifications.length > 0 ? (
            <AnimatePresence>
              {notifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <NotificationItem notification={notification} onNotificationClick={onNotificationClick} onDeleteNotification={onDeleteNotification} />
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <Bell className="mx-auto h-10 w-10 mb-2 text-gray-400" />
              No new notifications.
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default Notifications;
  
